import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import i18n from '@batical/i18n'
import { useContextValue } from '@batical/reducer/src'
import { CalculCodeStateKeys } from '@batical/types/src'
import { Box } from '@mui/material'
import { FaChevronRight } from 'react-icons/fa'

import { ViewLine, CalculText, CalculTextInput, SubCalculText, ViewDisclosure } from './LineStyleComponent'
import TextInputAdornment from './TextInputAdornment'

const isDouble = (value: unknown) => {
  return typeof value === 'number' || value instanceof Number
}
export type TextInputLineMinMaxProps = {
  calculCode: CalculCodeStateKeys
  /**
   * Unit value to display
   *
   * @type {string}
   */
  unit?: string
  /**
   * Should show a disclosure button on right side
   *
   * @type {boolean}
   */
  disclosure?: boolean

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDisclosurePress?: (value: number, meta: any) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  image?: any
  /**
   * Input Value can be negative
   *
   * @type {boolean}
   */
  negativeValue?: boolean
  /**
   *
   *
   * @type {string}
   */
  variableName: string
  /**
   *
   *
   * @type {number}
   */
  min?: number
  /**
   *
   *
   * @type {number}
   */
  max?: number
  /**
   *
   *
   * @type {number}
   */
  factor?: number
  /**
   * action to do on change value
   *
   */
  onChange?: (number: number, value: string, extra?: Object) => void
  extraParam?: Object //category can be used for disclosure, additional parameter for on change on disclosure
  doCalcul?: boolean
  first?: boolean
  last?: boolean
}

const TextInputLineMinMax = ({
  calculCode,
  variableName,
  unit = '',
  min,
  max,
  disclosure = false,
  onChange,
  extraParam = undefined,
  factor = 1,
  doCalcul = false,
  first = false,
  last = false
}: TextInputLineMinMaxProps) => {
  const [value, update] = useContextValue(variableName, calculCode, doCalcul)

  const textInputRef = useRef(null)
  const [inputVal, setInputVal] = useState('0')

  const text = useMemo(() => {
    return i18n.t(`${calculCode}_${variableName}`)
  }, [calculCode, variableName])

  const subtext = useMemo(() => {
    let sub
    if (min != null) {
      sub = `(min: ${min}${String(unit)}`
      if (max != null) {
        sub += ' - '
      } else {
        sub += ')'
      }
    }
    if (max != null) {
      if (min == null) {
        sub = '('
      }
      sub += `max: ${max}${String(unit)})`
    }
    return sub
  }, [max, min, unit])

  useEffect(() => {
    if (isDouble(value)) {
      setInputVal(String(+value * factor))
    }
  }, [value, factor])

  const _onEndEditing = useCallback(() => {
    let result = parseFloat(inputVal.replace(',', '.'))
    if (isNaN(result)) {
      setInputVal(String(+value))

      if (onChange) {
        onChange(0, variableName, extraParam)
      } else {
        update(0)
      }
    } else if (String(value) === inputVal) {
      // console.error('textinputline, I donw now why this condition but should not happen')
      // i saw it happen so it is required but did not analyse why
      if (onChange) {
        onChange(value / factor, variableName, extraParam)
      } else {
        update(value / factor)
      }

      setInputVal(inputVal)
    } else {
      if (min && result < min) {
        result = min
      } else if (max && result > max) {
        result = max
      }
      if (onChange) {
        onChange(result / factor, variableName, extraParam)
      } else {
        update(result / factor)
      }
    }
  }, [extraParam, factor, inputVal, max, min, onChange, update, value, variableName])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const _onChangeText = useCallback((event: any) => {
    setInputVal(event.target.value)
  }, [])

  const onFocus = useCallback(() => {
    setInputVal('')
  }, [])

  // const _onTouchCell = useCallback(() => {
  //   if (onDisclosurePress != null) {
  //     let val = 0 || parseFloat(inputVal.replace(',', '.'))
  //     // if (value != null) {
  //     onDisclosurePress(val, extraParam)
  //     //  }
  //   } else {
  //     //textInputRef?.current?.focus()
  //   }
  // }, [extraParam, inputVal, onDisclosurePress])
  const onKeypress = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any) => {
      if (e.key === 'Enter') {
        _onEndEditing()
      }
    },
    [_onEndEditing]
  )
  return (
    <ViewLine first={first} last={last}>
      <Box>
        <CalculText>{text}</CalculText>
        {subtext && <SubCalculText>{subtext}</SubCalculText>}
      </Box>
      <ViewDisclosure>
        <CalculTextInput
          ref={textInputRef}
          value={inputVal}
          onFocus={onFocus}
          onChange={_onChangeText}
          onKeyPress={onKeypress}
          onBlur={_onEndEditing}
          variant="outlined"
          size="small"
          InputProps={{
            endAdornment: <TextInputAdornment text={unit} />
          }}
        />

        {disclosure && <FaChevronRight color="#808080" size={25} />}
      </ViewDisclosure>
    </ViewLine>
  )
}

export default memo(TextInputLineMinMax)
